import { useMemo, useState } from "react";

import {
  ChipList,
  StatusBadge,
  TypeBadge,
} from "@/app/library/components/molecules";
import {
  getMockTemplatePackVersion,
  isMockTemplatePackAllowedForPlan,
  mockAccessPresets,
  mockTemplatePacks,
} from "@/app/library/data_sources/mock";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import type { APIPlanDefinitionResponseType } from "@/core/data_sources/models/APIPlanDefinition";
import type { Plan } from "@/app/plans/utils";
import { AccessPresetsEditor } from "./AccessPresetsEditor";
import { Badge, Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  AdminStatCard,
  Banner,
} from "@/core/ui/molecules";

type PlanEditorDialogProps = {
  readonly apiPlan?: APIPlanDefinitionResponseType;
  readonly onClose: () => void;
  readonly plan: Plan;
};

type PlanTab =
  | "access"
  | "audit"
  | "capabilities"
  | "limits"
  | "models"
  | "packs"
  | "ui";

const tabs: readonly [PlanTab, string][] = [
  ["capabilities", "Capabilities"],
  ["models", "Models & Customer"],
  ["limits", "Limits & Branding"],
  ["ui", "UI Configuration"],
  ["access", "Access Presets"],
  ["packs", "Template Packs"],
];

const moduleKeys = [
  "work_orders",
  "schedule",
  "customers",
  "invoicing",
  "forms",
  "inventory",
  "maintenance",
  "projects",
  "assets_advanced",
  "documents",
  "notifications",
  "vendors",
] as const;

const flagKeys = [
  "embedded_report",
  "timesheet",
  "forms_library",
  "forms_editable",
  "form_builder",
  "media_assignable",
  "project_templates",
  "model_builder",
  "inventory_check",
  "advanced_assets",
  "client_portal",
  "stripe_payments",
] as const;

export function PlanEditorDialog({
  apiPlan,
  onClose,
  plan,
}: PlanEditorDialogProps) {
  const [activeTab, setActiveTab] = useState<PlanTab>("capabilities");
  const allowedPacks = useMemo(
    () =>
      mockTemplatePacks.filter((pack) =>
        isMockTemplatePackAllowedForPlan(pack, plan.name),
      ),
    [plan.name],
  );
  const selectedTemplates = useMemo(
    () => allowedPacks.flatMap((pack) => pack.templates ?? []),
    [allowedPacks],
  );
  const enabledModules = moduleKeys.filter((key) =>
    isEnabledForPlan(plan, key),
  ).length;
  const enabledFlags = flagKeys.filter((key) =>
    isEnabledForPlan(plan, key),
  ).length;

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[1180px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">
            Plan Editor · {plan.name}
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            Plan Engine · v{plan.activeVersion} · {plan.tenantsCount} tenants ·
            Published
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="mb-4 flex flex-wrap gap-1 border-b border-border">
          {tabs.map(([tab, label]) => (
            <button
              className={`border-b-2 px-3.5 py-2.5 text-[13px] font-extrabold ${
                activeTab === tab
                  ? "border-cta text-cta"
                  : "border-transparent text-ink-muted"
              }`}
              key={tab}
              onClick={() => setActiveTab(tab)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        {activeTab === "capabilities" ? (
          <CapabilitiesTab
            enabledFlags={enabledFlags}
            enabledModules={enabledModules}
            packCount={allowedPacks.length}
            plan={plan}
          />
        ) : null}
        {activeTab === "models" ? (
          <ModelsTab plan={plan} selectedTemplates={selectedTemplates} />
        ) : null}
        {activeTab === "limits" ? <LimitsTab plan={plan} /> : null}
        {activeTab === "ui" ? <UITab plan={plan} /> : null}
        {activeTab === "access" ? (
          <AccessTab apiPlan={apiPlan} plan={plan} />
        ) : null}
        {activeTab === "packs" ? (
          <PacksTab allowedPacks={allowedPacks} plan={plan} />
        ) : null}
        {activeTab === "audit" ? (
          <AuditTab
            enabledFlags={enabledFlags}
            enabledModules={enabledModules}
            packCount={allowedPacks.length}
            plan={plan}
          />
        ) : null}
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button onClick={onClose}>Close</Button>
        <Button onClick={() => setActiveTab("audit")}>Audit History</Button>
        <Button>Save Draft</Button>
        <Button variant="primary">Publish</Button>
      </footer>
    </div>
  );
}

function CapabilitiesTab({
  enabledFlags,
  enabledModules,
  packCount,
  plan,
}: {
  readonly enabledFlags: number;
  readonly enabledModules: number;
  readonly packCount: number;
  readonly plan: Plan;
}) {
  return (
    <div>
      <Banner tone="ok">
        <strong>Plan Engine source of truth.</strong> Modules control access
        areas. Feature flags refine behavior inside modules. Frontend
        represents; backend enforces.
      </Banner>
      <div className="mb-[18px] grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Enabled modules" value={enabledModules} />
        <AdminStatCard label="Enabled flags" value={enabledFlags} />
        <AdminStatCard label="Allowed packs" value={packCount} />
        <AdminStatCard label="Tenants affected" value={plan.tenantsCount} />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TogglePanel
          items={moduleKeys.map((key) => [key, isEnabledForPlan(plan, key)])}
          title="Modules Included"
        />
        <TogglePanel
          items={flagKeys.map((key) => [key, isEnabledForPlan(plan, key)])}
          title="Feature Flags"
        />
      </div>
      <AdminPanel className="mt-3 p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Plan Header
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReadOnlyField label="Plan name" value={plan.name} />
          <ReadOnlyField
            label="Stripe product / billing mode"
            value={
              plan.name === "Enterprise"
                ? "Manual / SOW"
                : `Stripe product ${plan.id}`
            }
          />
          <ReadOnlyField
            label="Internal description"
            value={`${plan.name} operating configuration`}
          />
          <ReadOnlyField
            label="Commercial description"
            value={plan.commercial}
          />
        </div>
      </AdminPanel>
    </div>
  );
}

function ModelsTab({
  plan,
  selectedTemplates,
}: {
  readonly plan: Plan;
  readonly selectedTemplates: readonly {
    readonly description?: string;
    readonly name?: string;
    readonly status?: string;
    readonly type?: string;
    readonly version?: number;
  }[];
}) {
  const modelTemplates = selectedTemplates.filter(
    (template) => template.type === "model",
  );

  return (
    <div>
      <AdminPanel className="p-[18px]">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-[15px] font-extrabold text-ink">
              Models Configuration <Badge tone="critical">Critical</Badge>
            </h3>
            <p className="text-xs text-ink-muted">
              Structure, usage_type and capabilities are defined in Library.
              This screen only controls availability through allowed packs.
            </p>
          </div>
          <Button variant="primary">Add Model Template</Button>
        </div>
        <AdminMiniList>
          {modelTemplates.map((template) => (
            <AdminMiniRow key={template.name}>
              <span>
                <strong>{template.name}</strong>
                <span className="block text-xs text-ink-muted">
                  {template.description} · v{template.version ?? 1}
                </span>
              </span>
              <span className="flex gap-1.5">
                <Badge tone="success">embedded</Badge>
                <Badge tone="info">Library</Badge>
              </span>
            </AdminMiniRow>
          ))}
        </AdminMiniList>
      </AdminPanel>
      <Banner>
        <strong>Tenant Setup installs templates.</strong> Install template =
        create ModelSchema + FieldModel + Component. Idempotent by template_id +
        version. No merge in V1.
      </Banner>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-2 text-[15px] font-extrabold text-ink">
          Customer Visibility
        </h3>
        <AdminMiniList>
          {[
            "Basic Info",
            "Contacts",
            "Locations",
            "Equipment",
            "Service History",
            "Documents",
            "Billing",
          ].map((item, index) => (
            <AdminMiniRow key={item}>
              <strong>{item}</strong>
              <input
                checked={index < 3 || plan.name !== "Go"}
                readOnly
                type="checkbox"
              />
            </AdminMiniRow>
          ))}
        </AdminMiniList>
      </AdminPanel>
    </div>
  );
}

function LimitsTab({ plan }: { readonly plan: Plan }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">Limits</h3>
        <ReadOnlyField
          label="Max users"
          value={getPlanLimit(plan.name, "users")}
        />
        <ReadOnlyField
          label="Max workspaces"
          value={getPlanLimit(plan.name, "workspaces")}
        />
        <ReadOnlyField
          label="Storage GB"
          value={getPlanLimit(plan.name, "storage")}
        />
        <ReadOnlyField label="Trial days" value="14" />
      </AdminPanel>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Billing & Branding
        </h3>
        <ReadOnlyField label="Price" value={plan.price} />
        <ReadOnlyField
          label="Billing mode"
          value={plan.name === "Enterprise" ? "Manual / Enterprise" : "Stripe"}
        />
        <ReadOnlyField
          label="Product domain"
          value={
            plan.name === "Enterprise" ? "sitehubpro.com" : "getsitehub.com"
          }
        />
      </AdminPanel>
    </div>
  );
}

function UITab({ plan }: { readonly plan: Plan }) {
  return (
    <div>
      <Banner>
        <strong>
          UI Configuration only changes customer-facing labels and default
          visibility.
        </strong>{" "}
        Technical module, entity and column keys never change.
      </Banner>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Module Menu Naming
        </h3>
        <AdminMiniList>
          {["work_orders", "customers", "forms", "inventory", "projects"].map(
            (key) => (
              <AdminMiniRow key={key}>
                <strong>{key}</strong>
                <span>
                  {key === "work_orders" && plan.name !== "Enterprise"
                    ? "Jobs"
                    : key}
                </span>
              </AdminMiniRow>
            ),
          )}
        </AdminMiniList>
      </AdminPanel>
    </div>
  );
}

function AccessTab({
  apiPlan,
  plan,
}: {
  readonly apiPlan?: APIPlanDefinitionResponseType;
  readonly plan: Plan;
}) {
  if (apiPlan?.id != null) {
    return (
      <AccessPresetsEditor
        initialConfig={apiPlan.config}
        onSaved={() => undefined}
        planId={apiPlan.id}
      />
    );
  }
  return (
    <div>
      <Banner tone="ok">
        <strong>Access Presets are installed at tenant creation.</strong> Roles
        stay fixed in code: Owner, Supervisor, Member, Viewer. Presets are
        copied into the tenant.
      </Banner>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Permission Profile Presets
        </h3>
        <AdminMiniList>
          {mockAccessPresets.map((preset) => (
            <AdminMiniRow key={preset.id}>
              <span>
                <strong>{preset.name}</strong>
                <span className="block text-xs text-ink-muted">
                  Role: {preset.role} · Plans: {preset.plans.join(", ")}
                </span>
              </span>
              <input
                checked={preset.plans.includes(plan.name)}
                readOnly
                type="checkbox"
              />
            </AdminMiniRow>
          ))}
        </AdminMiniList>
      </AdminPanel>
    </div>
  );
}

function PacksTab({
  allowedPacks,
  plan,
}: {
  readonly allowedPacks: readonly APILibraryTemplatePackResponseType[];
  readonly plan: Plan;
}) {
  return (
    <div>
      <Banner tone="warning">
        <strong>
          Plan allows packs. Tenant Setup installs packs atomically.
        </strong>{" "}
        No partial success. No merge in V1. ModelSchema.code conflict blocks
        installation.
      </Banner>
      <AdminPanel className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3.5 py-3.5">
          <div>
            <strong>Allowed Template Packs</strong>
            <div className="text-xs text-ink-muted">
              Catalogs are now read-only inside Library. This is the only place
              where a plan allows onboarding content.
            </div>
          </div>
          <Badge tone="info">{allowedPacks.length} allowed</Badge>
        </div>
        <div className="divide-y divide-border">
          {allowedPacks.map((pack) => (
            <div
              className="grid grid-cols-[minmax(0,1.4fr)_1fr_1fr_1fr_auto] items-center gap-3 px-3.5 py-3"
              key={pack.name}
            >
              <span>
                <strong>{pack.name}</strong>
                <span className="block text-xs text-ink-muted">
                  {pack.description} · {getMockTemplatePackVersion(pack)}
                </span>
              </span>
              <ChipList
                values={pack.verticals?.map((vertical) => vertical.name)}
              />
              <span>{contentsText(pack)}</span>
              <StatusBadge status={pack.status} />
              <Button size="sm" variant="danger">
                Remove
              </Button>
            </div>
          ))}
        </div>
      </AdminPanel>
      <Banner>
        <strong>Where did Catalogs go?</strong> Form, Media and Model Templates
        remain in Library as read-only catalog reference. Plan Editor does not
        assign individual templates. Plans allow Template Packs only.
      </Banner>
      <span className="sr-only">{plan.name}</span>
    </div>
  );
}

function AuditTab({
  enabledFlags,
  enabledModules,
  packCount,
  plan,
}: {
  readonly enabledFlags: number;
  readonly enabledModules: number;
  readonly packCount: number;
  readonly plan: Plan;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Draft / Publish
        </h3>
        <AdminMiniList>
          <AdminMiniRow>
            <strong>Active version</strong>
            <span>v{plan.activeVersion}</span>
          </AdminMiniRow>
          <AdminMiniRow>
            <strong>Draft status</strong>
            <StatusBadge status={plan.draft ? "Draft exists" : "No draft"} />
          </AdminMiniRow>
          <AdminMiniRow>
            <strong>Tenants affected</strong>
            <span>{plan.tenantsCount}</span>
          </AdminMiniRow>
        </AdminMiniList>
        <textarea
          className={`${adminInputClasses} mt-3 min-h-24 w-full py-2.5`}
          placeholder="Explain why this plan change is being published."
        />
      </AdminPanel>
      <AdminPanel className="p-[18px]">
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Impact Preview
        </h3>
        <AdminMiniList>
          <AdminMiniRow>
            <strong>Modules</strong>
            <span>{enabledModules} enabled</span>
          </AdminMiniRow>
          <AdminMiniRow>
            <strong>Feature flags</strong>
            <span>{enabledFlags} enabled</span>
          </AdminMiniRow>
          <AdminMiniRow>
            <strong>Allowed packs</strong>
            <span>{packCount}</span>
          </AdminMiniRow>
        </AdminMiniList>
      </AdminPanel>
    </div>
  );
}

function TogglePanel({
  items,
  title,
}: {
  readonly items: readonly (readonly [string, boolean])[];
  readonly title: string;
}) {
  return (
    <AdminPanel className="p-[18px]">
      <h3 className="mb-1 text-[15px] font-extrabold text-ink">{title}</h3>
      <p className="mb-3 text-xs text-ink-muted">
        Backend enforcement remains the source of truth.
      </p>
      <AdminMiniList>
        {items.map(([key, enabled]) => (
          <AdminMiniRow key={key}>
            <strong>{key}</strong>
            <input checked={enabled} readOnly type="checkbox" />
          </AdminMiniRow>
        ))}
      </AdminMiniList>
    </AdminPanel>
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | number;
}) {
  return (
    <label className="mb-3 block">
      <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
        {label}
      </span>
      <input className={adminInputClasses} readOnly value={value} />
    </label>
  );
}

function isEnabledForPlan(plan: Plan, key: string) {
  if (plan.name === "Enterprise") {
    return true;
  }
  if (plan.name === "Scale") {
    return !["vendors"].includes(key);
  }
  if (plan.name === "Growth") {
    return [
      "work_orders",
      "schedule",
      "customers",
      "forms",
      "inventory",
      "notifications",
      "forms_library",
      "forms_editable",
      "template_packs",
      "stripe_payments",
    ].includes(key);
  }
  return [
    "work_orders",
    "schedule",
    "customers",
    "forms",
    "notifications",
    "template_packs",
    "stripe_payments",
  ].includes(key);
}

function getPlanLimit(
  plan: Plan["name"],
  key: "storage" | "users" | "workspaces",
) {
  const limits = {
    Enterprise: {
      users: "Unlimited",
      workspaces: "Unlimited",
      storage: "Custom",
    },
    Go: { users: 5, workspaces: 1, storage: 10 },
    Growth: { users: 25, workspaces: 3, storage: 50 },
    Scale: { users: 100, workspaces: 10, storage: 250 },
  } as const;

  return limits[plan][key];
}

function contentsText(pack: (typeof mockTemplatePacks)[number]) {
  const templates = pack.templates ?? [];
  const forms = templates.filter((template) => template.type === "form").length;
  const media = templates.filter(
    (template) => template.type === "media",
  ).length;
  const models = templates.filter(
    (template) => template.type === "model",
  ).length;

  return (
    <span className="flex flex-wrap gap-1">
      <TypeBadge type="form" />
      <span>{forms}</span>
      <TypeBadge type="media" />
      <span>{media}</span>
      <TypeBadge type="model" />
      <span>{models}</span>
    </span>
  );
}
